export type Lang = 'tr' | 'en';

export const LANGUAGES: { code: Lang; label: string }[] = [
  { code: 'tr', label: 'TR' },
  { code: 'en', label: 'EN' },
];

export const DEFAULT_LANG: Lang = 'tr';

// English is the canonical key set.
const en = {
  'common.online': 'Online',
  'common.offline': 'Offline',
  'common.fullscreen': 'Fullscreen',
  'common.cancel': 'Cancel',

  'pairing.rotation': 'Screen rotation',

  'settings.title': 'Device settings',
  'settings.fullscreen': 'Toggle fullscreen',
  'settings.reload': 'Reload player',
  'settings.clearContent': 'Clear content & re-sync',
  'settings.reset': 'Reset device (unpair)',
  'settings.close': 'Close',
  'settings.confirmClear': 'Clear all downloaded content and re-sync now?',
  'settings.confirmReset': 'Reset this device? It will unpair and return to the pairing screen.',
  'settings.device': 'Device',
  'settings.version': 'Version',
  'settings.checkUpdate': 'Check for updates',
  'settings.checkingUpdate': 'Checking…',
  'settings.applyUpdate': 'Apply update & reload',
  'settings.applyingUpdate': 'Updating…',
  'settings.upToDate': 'Up to date',
  'settings.updateAvailable': 'Update ready',

  'pairing.prompt': 'Enter this code to pair your device',
  'pairing.or': 'or',
  'pairing.retry': 'Try again',
  'pairing.hintLabel': 'For setup and more information:',
  'pairing.version': 'VERSION',
  'pairing.status': 'STATUS',
  'pairing.ip': 'IP',
  'pairing.mac': 'MAC',
  'pairing.network': 'NETWORK',
  'pairing.connected': 'Connected',
  'pairing.noNetwork': 'No network',
  'pairing.expired': 'Pairing code has expired. Please try again.',
  'pairing.loginExisting': 'Log in to an existing device with a code',

  'network.ethernet': 'Ethernet',
  'network.wifi': 'Wi-Fi',
  'network.cellular': 'Cellular',

  'diag.open': 'Browser Compatibility',
  'diag.title': 'Browser Compatibility',
  'diag.close': 'Close',
  'diag.required': 'Required',
  'diag.optional': 'Optional',
  'diag.allOk': 'This browser supports everything the player needs.',
  'diag.missing': 'A required feature is missing; the player may not run correctly.',
  'diag.indexeddb': 'IndexedDB (offline storage)',
  'diag.video': 'Video playback (MP4 / H.264)',
  'diag.websocket': 'WebSocket (live updates)',
  'diag.serviceWorker': 'Service Worker (offline app shell)',
  'diag.cache': 'Cache Storage',
  'diag.storage': 'Storage estimate',
  'diag.playbackMode': 'Playback mode',
  'diag.tvMode': 'TV (single player)',
  'diag.standardMode': 'Standard (dual buffer)',

  'login.title': 'Enter the 6-digit code from your dashboard',
  'login.submit': 'Log in',
  'login.submitting': 'Logging in…',
  'login.back': 'Back',
  'login.invalid': 'Please enter the 6-digit code.',
  'login.error': 'Login failed. Please check the code and try again.',

  'loading.preparing': 'Setting up your screen…',
  'loading.downloading': 'Downloading content',

  'player.noContent': 'No content to display.',
} as const;

export type TKey = keyof typeof en;

const tr: Record<TKey, string> = {
  'common.online': 'Çevrimiçi',
  'common.offline': 'Çevrimdışı',
  'common.fullscreen': 'Tam ekran',
  'common.cancel': 'İptal',

  'pairing.rotation': 'Ekran yönü',

  'settings.title': 'Cihaz ayarları',
  'settings.fullscreen': 'Tam ekranı aç/kapat',
  'settings.reload': 'Oynatıcıyı yeniden başlat',
  'settings.clearContent': 'İçeriği temizle ve yenile',
  'settings.reset': 'Cihazı sıfırla (eşleştirmeyi kaldır)',
  'settings.close': 'Kapat',
  'settings.confirmClear': 'İndirilen tüm içerik temizlenip şimdi yeniden indirilsin mi?',
  'settings.confirmReset':
    'Cihaz sıfırlansın mı? Eşleştirme kaldırılacak ve eşleştirme ekranına dönülecek.',
  'settings.device': 'Cihaz',
  'settings.version': 'Sürüm',
  'settings.checkUpdate': 'Güncellemeleri denetle',
  'settings.checkingUpdate': 'Denetleniyor…',
  'settings.applyUpdate': 'Güncellemeyi uygula ve yeniden başlat',
  'settings.applyingUpdate': 'Güncelleniyor…',
  'settings.upToDate': 'Güncel',
  'settings.updateAvailable': 'Güncelleme hazır',

  'pairing.prompt': 'Cihazı eşleştirmek için bu kodu girin',
  'pairing.or': 'veya',
  'pairing.retry': 'Tekrar dene',
  'pairing.hintLabel': 'Detaylı bilgi ve kurulum için:',
  'pairing.version': 'SÜRÜM',
  'pairing.status': 'DURUM',
  'pairing.ip': 'IP',
  'pairing.mac': 'MAC',
  'pairing.network': 'AĞ',
  'pairing.connected': 'Bağlı',
  'pairing.noNetwork': 'Ağ yok',
  'pairing.expired': 'Eşleştirme kodunun süresi doldu. Lütfen tekrar deneyin.',
  'pairing.loginExisting': 'Mevcut bir cihaza kod ile giriş yap',

  'network.ethernet': 'Ethernet',
  'network.wifi': 'Wi-Fi',
  'network.cellular': 'Hücresel',

  'diag.open': 'Tarayıcı Uyumluluğu',
  'diag.title': 'Tarayıcı Uyumluluğu',
  'diag.close': 'Kapat',
  'diag.required': 'Gerekli',
  'diag.optional': 'İsteğe bağlı',
  'diag.allOk': 'Bu tarayıcı, oynatıcının ihtiyaç duyduğu her şeyi destekliyor.',
  'diag.missing': 'Gerekli bir özellik eksik; oynatıcı düzgün çalışmayabilir.',
  'diag.indexeddb': 'IndexedDB (çevrimdışı depolama)',
  'diag.video': 'Video oynatma (MP4 / H.264)',
  'diag.websocket': 'WebSocket (canlı güncelleme)',
  'diag.serviceWorker': 'Service Worker (çevrimdışı uygulama)',
  'diag.cache': 'Önbellek Depolama',
  'diag.storage': 'Depolama tahmini',
  'diag.playbackMode': 'Oynatma modu',
  'diag.tvMode': 'TV (tekli oynatıcı)',
  'diag.standardMode': 'Standart (çift tampon)',

  'login.title': 'Panonuzdaki 6 haneli kodu girin',
  'login.submit': 'Giriş yap',
  'login.submitting': 'Giriş yapılıyor…',
  'login.back': 'Geri',
  'login.invalid': 'Lütfen 6 haneli kodu girin.',
  'login.error': 'Giriş başarısız. Lütfen kodu kontrol edip tekrar deneyin.',

  'loading.preparing': 'Ekranınız hazırlanıyor…',
  'loading.downloading': 'İçerik indiriliyor',

  'player.noContent': 'Gösterilecek içerik yok.',
};

export const translations: Record<Lang, Record<TKey, string>> = { en, tr };
